/**
 * Shared Nivo theme for consistent chart styling across light/dark modes.
 * Colors are resolved from basis semantic CSS custom properties at render
 * time, so charts automatically track the active light/dark mode without a
 * static light/dark theme pair.
 */
import { useMemo } from 'react'
import type { PartialTheme } from '@nivo/theming'

/** Resolve a CSS custom property to a concrete color, following one or more
 * levels of `var(--x[, fallback])` indirection (basis light tokens alias the
 * base --color-* ramp; dark tokens are direct oklch; the ramp is direct).
 * Returns '' when unresolvable (no DOM / tokens not mounted). Exported so chart
 * components can resolve a token to a concrete Nivo color. */
export function readCssVar(name: string): string {
  if (typeof document === 'undefined') return ''
  const styles = getComputedStyle(document.documentElement)
  let value = styles.getPropertyValue(name).trim()
  let guard = 0
  while (value.startsWith('var(') && guard++ < 10) {
    const inner = value.slice(4, value.lastIndexOf(')'))
    const comma = inner.indexOf(',')
    const ref = (comma === -1 ? inner : inner.slice(0, comma)).trim()
    const fallback = comma === -1 ? '' : inner.slice(comma + 1).trim()
    value = styles.getPropertyValue(ref).trim() || fallback
  }
  // If the chain never resolved (exhausted the guard or is cyclic), return '' so
  // callers fall through to their neutral fallback rather than emitting a raw
  // `var(...)` string into an SVG fill/stroke.
  return value.startsWith('var(') ? '' : value
}

// Legacy palette — fallback when tokens can't be resolved (SSR / tests / unmounted).
const CHART_FALLBACK = ['#6366f1', '#06b6d4', '#f59e0b', '#10b981', '#f43f5e'] as const

/** Series/categorical palette resolved from basis --chart-1..5, cycled out to 8
 * slots so existing direct indexes (e.g. chartColors[4], CampaignComparison) stay
 * in-bounds and behavior matches the previous 8-length palette. 8→5 distinct-color
 * reduction: slots 5-7 repeat colors 1-3. */
export function getChartColors(): string[] {
  const resolved = ['--chart-1', '--chart-2', '--chart-3', '--chart-4', '--chart-5'].map(
    (n, i) => readCssVar(n) || CHART_FALLBACK[i],
  )
  return [0, 1, 2, 3, 4, 0, 1, 2].map((i) => resolved[i])
}

// Neutral fallbacks (light-ish) mirror the previous light theme, used only when a token is unresolvable.
const FALLBACK = { mutedFg: '#6b7280', border: '#e5e7eb', foreground: '#374151', popover: '#ffffff', popoverFg: '#374151' }

/** Build the Nivo theme from basis semantic tokens, resolved at call time so it
 * reflects the active light/dark mode (tokens flip via the `.dark` class). */
export function buildNivoTheme(): PartialTheme {
  const mutedFg = readCssVar('--muted-foreground') || FALLBACK.mutedFg
  const border = readCssVar('--border') || FALLBACK.border
  const foreground = readCssVar('--foreground') || FALLBACK.foreground
  const popover = readCssVar('--popover') || FALLBACK.popover
  const popoverFg = readCssVar('--popover-foreground') || FALLBACK.popoverFg
  return {
    text: { fontSize: 11, fill: mutedFg },
    axis: {
      domain: { line: { stroke: border, strokeWidth: 1 } },
      ticks: { line: { stroke: border, strokeWidth: 1 }, text: { fontSize: 10, fill: mutedFg } },
      legend: { text: { fontSize: 12, fill: mutedFg } },
    },
    grid: { line: { stroke: border, strokeWidth: 1 } },
    crosshair: { line: { stroke: mutedFg, strokeWidth: 1, strokeOpacity: 0.5 } },
    tooltip: {
      container: {
        background: popover, color: popoverFg, fontSize: 12,
        borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '8px 12px',
      },
    },
    labels: { text: { fontSize: 11, fill: foreground } },
    legends: { text: { fontSize: 11, fill: mutedFg } },
  }
}

/** Nivo theme for the active mode. */
export function useNivoTheme(): PartialTheme {
  return buildNivoTheme()
}

/** Series/categorical palette (basis --chart-1..5, cycled), resolved fresh on
 * every render — after a light/dark flip, charts pick up the new palette on
 * their next render (nothing here forces that render; same contract as
 * useNivoTheme). Referentially stable while values are unchanged, so it is
 * safe in memo deps. */
export function useChartColors(): string[] {
  const key = getChartColors().join('|')
  return useMemo(() => key.split('|'), [key])
}
